import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}

/**
 * Lazy host for <shopify-account>. The real component sits in a child <template>;
 * this swaps it in on pointer intent (pointerover fires before pointerdown on touch
 * too, so the tap that follows lands on the live component) or, failing that, five
 * seconds after load. Until then the child is a plain link to the account page.
 * Why: hydrating the account bundle at load cost ~300 KB, two fonts and 54 requests
 * on every page (WS20 audit, 2026-09-16).
 */
class RAccountLazy extends HTMLElement {
  #done = false;

  connectedCallback() {
    this.addEventListener('pointerover', this.#upgrade, { once: true, passive: true });
    const later = () => setTimeout(this.#upgrade, 5000);
    if (document.readyState === 'complete') later();
    else window.addEventListener('load', later, { once: true });
  }

  #upgrade = () => {
    if (this.#done) return;
    this.#done = true;
    const template = this.querySelector(':scope > template');
    if (!(template instanceof HTMLTemplateElement)) return;
    this.replaceChildren(template.content.cloneNode(true));
  };
}

if (!customElements.get('r-account-lazy')) {
  customElements.define('r-account-lazy', RAccountLazy);
}
